import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PublicCompanySettings {
  showResults: boolean;
  isLoading: boolean;
}

/**
 * Hook to fetch company settings for public-facing pages (like thank-you pages)
 * Uses the existing public RLS policy on companies table
 */
export const usePublicCompanySettings = (companyId: string | null): PublicCompanySettings => {
  const [showResults, setShowResults] = useState(true); // Default to true for legacy/self-directed assessments
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      // No company ID means self-directed assessment - show full results
      if (!companyId) {
        setShowResults(true);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('companies')
          .select('show_results_to_candidates')
          .eq('id', companyId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching company settings:', error);
          // Default to showing results on error
          setShowResults(true);
        } else if (data) {
          // Use the setting if it exists, otherwise default to false (new column default)
          setShowResults(data.show_results_to_candidates ?? false);
        } else {
          // Company not found - show results (legacy behavior)
          setShowResults(true);
        }
      } catch (err) {
        console.error('Error in usePublicCompanySettings:', err);
        setShowResults(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanySettings();
  }, [companyId]);

  return { showResults, isLoading };
};
